import { Fragment, useMemo } from 'react'
import { addDays, endOfDay, isToday, startOfDay } from 'date-fns'
import TodayTypo from '../../components/common/TodayTypo'
import { ProcessedEvent } from '../../types'
import { TableGrid } from '../../components/common/TableGrid'
import { MULTI_DAY_EVENT_HEIGHT } from '../../helpers/constants'
import { StackEvents } from '../../components/events/StackEvents'
import { MultiDayEvents } from '../../components/events/MultiDayEvents'
import {
  useSchedulerDispatchContext,
  useSchedulerPropsContext,
} from '../../context/schedulerContext'
import { ISODateString } from '@pogokid/util/datetime'
import { DayProps, DEFAULT_DAY_OPTIONS } from './types'
import {
  filterMultiDayEventsOnDays,
  filterSingleDayEventsByDay,
} from '../../helpers/eventFilters.ts'

function useDayStackedData(
  day: DayProps | null | undefined,
  selectedDate: ISODateString
) {
  const options = day ?? DEFAULT_DAY_OPTIONS
  const selectedDateDayStart = startOfDay(new Date(selectedDate))
  const daysList =
    options.plusDays > 0
      ? Array.from({ length: options.plusDays + 1 }).map(
          (_d, i) => addDays(selectedDateDayStart, i)
        )
      : [selectedDateDayStart]
  const dayStart = startOfDay(selectedDateDayStart)
  const dayEnd = endOfDay(daysList[daysList.length - 1])

  return {
    daysList,
    dayStart,
    dayEnd,
  }
}

export const DayStacked = () => {
  const { selectedDate, day, events, calendars, locale } =
    useSchedulerPropsContext()

  const { handleGotoDay } = useSchedulerDispatchContext()

  const { daysList, dayStart, dayEnd } = useDayStackedData(
    day,
    selectedDate
  )

  const visibleEvents = events.filter((event) =>
    calendars.has(event.calendarId)
  )

  const allMultiDayEventsAcrossTheDay =
    filterMultiDayEventsOnDays(daysList, visibleEvents)

  // Equalizing multi-day section height
  const headerHeight =
    MULTI_DAY_EVENT_HEIGHT *
      allMultiDayEventsAcrossTheDay.length +
    45

  return (
    <TableGrid days={daysList.length} indent={''}>
      {/* Header days */}
      {daysList.map((date, i) => (
        <span
          key={i}
          className={`rs__cell rs__header ${
            isToday(date) ? 'rs__today_cell' : ''
          }`}
          style={{ height: headerHeight }}
        >
          <TodayTypo
            date={date}
            onClick={handleGotoDay}
            locale={locale}
          />
          <MultiDayEvents
            events={visibleEvents}
            allWeekMulti={allMultiDayEventsAcrossTheDay}
            today={date}
            weekStart={dayStart}
            weekEnd={dayEnd}
          />
        </span>
      ))}

      <Fragment>
        {daysList.map((date) => (
          <DayStackDay
            key={date.valueOf()}
            date={date}
            recoursedEvents={visibleEvents}
          />
        ))}
      </Fragment>
    </TableGrid>
  )
}

interface DayStackDayProps {
  date: Date
  recoursedEvents: ProcessedEvent[]
}

function DayStackDay({
  date,
  recoursedEvents,
}: DayStackDayProps) {
  const todayEvents = useMemo(
    () => filterSingleDayEventsByDay(date, recoursedEvents),
    [date, recoursedEvents]
  )
  return (
    <span
      className={`rs__cell ${
        isToday(date) ? 'rs__today_cell' : ''
      }`}
    >
      <StackEvents todayEvents={todayEvents} />
    </span>
  )
}
